// Import necessary React hooks and context utilities
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

// Create a context for managing process state across components
const ProcessContext = createContext();

/**
 * Process shape: { id, name, status, progress?, controller?, index? }
 * Status: 'running' | 'success' | 'error' | 'pending'
 * @typedef {Object} Process
 * @property {number} id - Unique identifier for the process
 * @property {string} name - Name of the process
 * @property {'running' | 'success' | 'error' | 'pending'} status - Current status of the process
 * @property {number} [progress] - Optional progress percentage
 * @property {AbortController} [controller] - Optional controller for aborting the process
 * @property {number} [index] - Optional index for ordering or reference
 */

// ProcessProvider component to manage process state and provide context
export const ProcessProvider = ({ children }) => {
  // Initialize processes state from localStorage, setting status to 'pending' and clearing controllers
  const [processes, setProcesses] = useState(() => {
    const saved = localStorage.getItem('processes');
    if (saved) {
      return JSON.parse(saved).map((proc) => ({
        ...proc,
        status: 'pending',
        controller: undefined,
      }));
    }
    return [];
  });

  // Persist processes to localStorage whenever they change, excluding non-serializable fields
  useEffect(() => {
    const serializable = processes.map(({ id, name, status, progress, index }) => ({
      id,
      name,
      status,
      progress,
      index,
    }));
    localStorage.setItem('processes', JSON.stringify(serializable));
  }, [processes]);

  // Add a new process with a unique ID and 'running' status
  const addProcess = useCallback((proc) => {
    const id = Date.now();
    setProcesses((p) => [...p, { id, status: 'running', ...proc }]);
    return id;
  }, []);

  // Update an existing process by ID with provided updates
  const updateProcess = useCallback((id, updates) => {
    setProcesses((p) => p.map((pr) => (pr.id === id ? { ...pr, ...updates } : pr)));
  }, []);

  // Remove a process by ID
  const removeProcess = useCallback((id) => {
    setProcesses((p) => p.filter((pr) => pr.id !== id));
  }, []);

  // Cancel a running process by ID, aborting its controller and marking it as errored
  const cancelProcess = useCallback(
    (id) => {
      setProcesses((prev) => {
        const process = prev.find((p) => p.id === id && p.status === 'running');
        if (process && process.controller) {
          process.controller.abort();
          return prev.map((p) =>
            p.id === id ? { ...p, status: 'error', error: 'Download cancelled' } : p
          );
        }
        return prev;
      });
      // Remove the process after a delay to allow UI feedback
      setTimeout(() => removeProcess(id), 2000);
    },
    [removeProcess]
  );

  // Provide process context to child components
  return (
    <ProcessContext.Provider
      value={{ processes, addProcess, updateProcess, removeProcess, cancelProcess }}
    >
      {children}
    </ProcessContext.Provider>
  );
};

// Custom hook to access the ProcessContext
export const useProcess = () => useContext(ProcessContext);